import { useState, useRef, Dispatch, SetStateAction } from "react";
import {
  DialogBody,
  DialogHeader,
  DialogTitle,
  FormDialog,
} from "@capacit-com/lara-law-shared-ui";
import PromptLibraryListView from "@/pages/chat/components/prompt-library/PromptLibraryListView";
import PromptLibraryPreview from "@/pages/chat/components/prompt-library/PromptLibraryPreview";
import Prompt from "@/models/prompt/prompt";
import { useContent } from "@/hooks/useContent";
import PromptLibraryForm from "@/pages/chat/components/prompt-library/PromptLibraryForm";
import PromptLibraryIcon from "../../components/prompt-library/PromptLibraryIcon";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPromptSelect?: (promptText: string) => void;
};

const PromptLibraryDialog = ({ open, onOpenChange, onPromptSelect }: Props) => {
  const { t } = useContent();
  const [promptFormState, setPromptFormState] = useState<
    "list" | "create" | "edit" | "preview"
  >("list");
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | undefined>(
    undefined
  );
  const [formHasChanged, setFormHasChanged] = useState(false);
  // Applying a prompt hands focus (and the caret) to the chat input, so the
  // dialog must not pull it back to its trigger on close (LAR-128).
  const promptAppliedRef = useRef(false);

  const isFormOpen = promptFormState === "create" || promptFormState === "edit";

  const close = () => {
    setPromptFormState("list");
    setSelectedPrompt(undefined);
    setFormHasChanged(false);
    onOpenChange(false);
  };

  const backToList = () => {
    setPromptFormState("list");
    setSelectedPrompt(undefined);
    setFormHasChanged(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      isDirty={isFormOpen && formHasChanged}
      size="xl"
      onCloseAutoFocus={(event) => {
        if (promptAppliedRef.current) {
          promptAppliedRef.current = false;
          event.preventDefault();
        }
      }}
    >
      <DialogHeader>
        <DialogTitle className="text-xl font-semibold flex items-center gap-2 text-foreground">
          <PromptLibraryIcon className="size-5 text-foreground" />
          {promptFormState === "list"
            ? t("chat.emptyState.cards.prompts.title")
            : promptFormState === "create"
              ? "Opret ny prompt"
              : promptFormState === "edit"
                ? "Rediger prompt"
                : "Forhåndsvis prompt"}
        </DialogTitle>
      </DialogHeader>
      <DialogBody className="flex flex-col">
        {promptFormState === "list" && (
          <PromptLibraryListView
            setInputValue={
              ((text: string | ((prev: string) => string)) => {
                const promptText = typeof text === "string" ? text : "";
                if (onPromptSelect && promptText) {
                  promptAppliedRef.current = true;
                  onPromptSelect(promptText);
                }
                close();
              }) as Dispatch<SetStateAction<string>>
            }
            handleEditPromptClicked={(prompt: Prompt) => {
              setSelectedPrompt(prompt);
              setPromptFormState("edit");
            }}
            handleNewPromptClicked={() => {
              setSelectedPrompt(undefined);
              setPromptFormState("create");
            }}
            handlePreviewPrompt={(prompt: Prompt) => {
              setSelectedPrompt(prompt);
              setPromptFormState("preview");
            }}
            closePopover={close}
          />
        )}
        {promptFormState === "create" && (
          <PromptLibraryForm
            selectedPrompt={undefined}
            handleBackClicked={backToList}
            onFormChange={setFormHasChanged}
          />
        )}
        {promptFormState === "edit" && selectedPrompt && (
          <PromptLibraryForm
            selectedPrompt={selectedPrompt}
            handleBackClicked={backToList}
            onFormChange={setFormHasChanged}
          />
        )}
        {promptFormState === "preview" && selectedPrompt && (
          <PromptLibraryPreview
            selectedPrompt={selectedPrompt}
            handleBackClicked={backToList}
          />
        )}
      </DialogBody>
    </FormDialog>
  );
};

export default PromptLibraryDialog;
